import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('shieldpay_token'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);

    // Axios instance with auth header
    const authAxios = axios.create({
        baseURL: API,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    // Update axios headers when token changes
    useEffect(() => {
        if (token) {
            authAxios.defaults.headers.Authorization = `Bearer ${token}`;
        } else {
            delete authAxios.defaults.headers.Authorization;
        }
    }, [token]);

    // Fetch user on mount if token exists
    useEffect(() => {
        const fetchUser = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const response = await authAxios.get('/auth/me');
                setUser(response.data);
            } catch (err) {
                console.error('Auth fetch error:', err);
                localStorage.removeItem('shieldpay_token');
                setToken(null);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, []);

    const sendOTP = async (phone) => {
        setError(null);
        try {
            const response = await axios.post(`${API}/auth/send-otp`, { phone });
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to send OTP';
            setError(message);
            throw new Error(message);
        }
    };

    const verifyOTP = async (phone, otp) => {
        setError(null);
        try {
            const response = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
            const { token: newToken, user: userData } = response.data;
            
            localStorage.setItem('shieldpay_token', newToken);
            setToken(newToken);
            setUser(userData);
            
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to verify OTP';
            setError(message);
            throw new Error(message);
        }
    };

    const updateProfile = async (data) => {
        setError(null);
        try {
            const response = await authAxios.put('/auth/profile', data);
            setUser(response.data);
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to update profile';
            setError(message);
            throw new Error(message);
        }
    };

    const logout = () => {
        localStorage.removeItem('shieldpay_token');
        setToken(null);
        setUser(null);
        setNotificationsEnabled(false);
    };

    // Register FCM token for push notifications
    const registerFCMToken = async (fcmToken) => {
        try {
            await authAxios.post('/notifications/register-fcm-token', { fcm_token: fcmToken });
            setNotificationsEnabled(true);
            return true;
        } catch (err) {
            console.error('Failed to register FCM token:', err);
            return false;
        }
    };

    // Unregister FCM token
    const unregisterFCMToken = async () => {
        try {
            await authAxios.delete('/notifications/unregister-fcm-token');
            setNotificationsEnabled(false);
            return true;
        } catch (err) {
            console.error('Failed to unregister FCM token:', err);
            return false;
        }
    };

    const value = {
        user,
        token,
        loading,
        error,
        isAuthenticated: !!user,
        notificationsEnabled,
        sendOTP,
        verifyOTP,
        updateProfile,
        logout,
        authAxios,
        registerFCMToken,
        unregisterFCMToken
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
